import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterNegotiationService } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { useCollectionResponsibleOptions } from "../../hooks/useCollectionResponsibleOptions";
import { SECTOR_OPTIONS, SinisterSector, getSectorLabel } from "../../constants/sectorOptions";
import { FollowUpGenerationOptions } from "../FollowUpGenerationOptions";
import { generateFollowUps } from "../../utils/generateFollowUps";

type ResponsibleOption = { id: string; label: string };

interface Props {
	open: boolean;
	sinisterId: string;
	currentSector: SinisterSector;
	onClose: () => void;
	onConfirm: (sector: SinisterSector) => void;
}

export const ModalChangeSector = ({ open, sinisterId, currentSector, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [sector, setSector] = useState<SinisterSector>(currentSector);
	const [generateFollowUp, setGenerateFollowUp] = useState(true);
	// Follow up externo é visível pro sinistro e pra imobiliária (e dispara e-mail aos contatos
	// cadastrados) — por isso começa desmarcado, é uma escolha explícita de cada lançamento.
	const [generateExternalFollowUp, setGenerateExternalFollowUp] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [responsibleSearch, setResponsibleSearch] = useState("");
	const [selectedResponsible, setSelectedResponsible] = useState<ResponsibleOption | null>(null);
	// Opções de responsável dependem do setor ESCOLHIDO no formulário (não do setor atual), já que o
	// objetivo é permitir delegar direto o responsável do setor de destino ao mudar de setor.
	const { responsibleOptions, loading: loadingResponsibles } = useCollectionResponsibleOptions(sector, responsibleSearch);

	useEffect(() => {
		if (open) {
			setSector(currentSector);
			setGenerateFollowUp(true);
			setGenerateExternalFollowUp(false);
			setResponsibleSearch("");
			setSelectedResponsible(null);
		}
	}, [open, currentSector]);

	// Trocar o setor no formulário invalida a seleção de responsável anterior (era de outro setor).
	useEffect(() => {
		setSelectedResponsible(null);
	}, [sector]);

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const result = await trackAsync("sinister.change_sector", () => SinisterService.updateSinisterSetor(sinisterId, sector), { sinisterId });

		if (result instanceof Error) {
			setIsSubmitting(false);
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		if (selectedResponsible) {
			// Ação secundária: se falhar, não deve derrubar o sucesso da ação principal (setor já foi salvo).
			try {
				await trackAsync(
					"negotiation.update_collection_responsible",
					() => SinisterNegotiationService.updateCollectionResponsible(sinisterId, selectedResponsible.id),
					{ sinisterId }
				);
			} catch (error) {
				console.error("Falha ao delegar responsável junto com a mudança de setor:", error);
			}
		}

		await generateFollowUps({
			sinisterId,
			note: `<p>Setor atualizado para: ${getSectorLabel(sector)}</p>${
				selectedResponsible ? `<p>Responsável pela cobrança: ${selectedResponsible.label}</p>` : ""
			}`,
			generateInternal: generateFollowUp,
			generateExternal: generateExternalFollowUp,
			trackLabel: "change_sector",
		});

		setIsSubmitting(false);

		onConfirm(sector);
		toastHandler.present({
			type: "success",
			messageOrContent: `Setor atualizado para ${getSectorLabel(sector)}.`,
			position: "top-right",
		});
		onClose();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
					Mudar de Setor
				</Typography>
				<IconButton onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2} mt={0.5}>
					<Typography variant="body2" color="text.secondary">
						Selecione o setor responsável por esse sinistro.
					</Typography>
					<TextField select label="Setor" value={sector} onChange={(e) => setSector(e.target.value as SinisterSector)} fullWidth>
						{SECTOR_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
					{sector === currentSector && (
						<Typography variant="caption" color="text.secondary">
							O sinistro já está no setor selecionado.
						</Typography>
					)}

					<Autocomplete
						options={responsibleOptions}
						value={selectedResponsible}
						loading={loadingResponsibles}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						getOptionLabel={(option) => option.label}
						onChange={(_, value) => setSelectedResponsible(value)}
						onInputChange={(_, value, reason) => {
							if (reason === "input") setResponsibleSearch(value);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Responsável (opcional)"
								placeholder="Delegar responsável pelo novo setor"
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{loadingResponsibles ? <CircularProgress size={20} /> : null}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
					/>

					<FollowUpGenerationOptions
						generateInternal={generateFollowUp}
						onChangeInternal={setGenerateFollowUp}
						generateExternal={generateExternalFollowUp}
						onChangeExternal={setGenerateExternalFollowUp}
					/>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose}>Cancelar</Button>
				<Button variant="contained" disabled={isSubmitting || sector === currentSector} onClick={handleConfirm}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
		</>
	);
};
